import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Sparkles, Loader2, ClipboardCheck, Plus, Trash2, RefreshCw, GripVertical } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useCourseContext } from "@/contexts/CourseContext";

interface ExamQuestion {
  id: string;
  exam_id: string;
  question_type: "mcq" | "true_false" | "short_answer" | "fill_in_blank" | "capstone_essay";
  prompt: string;
  options: string[] | null;
  correct_answer: string;
  explanation: string | null;
  points: number;
  created_at?: string;
}

interface Exam {
  id: string;
  skill_node_id: string;
  passing_score: number;
  instructions: string | null;
  difficulty?: string | null;
  question_count?: number | null;
}

interface SkillNode {
  id: string;
  title: string;
  description: string | null;
  is_key_node: boolean;
}

const ManageNodeExam = () => {
  const { nodeId, projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCourseContext, clearCourseContext } = useCourseContext();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [node, setNode] = useState<SkillNode | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);
  const [editedPassingScore, setEditedPassingScore] = useState(70);
  const [editedInstructions, setEditedInstructions] = useState("");
  const [courseName, setCourseName] = useState<string | null>(null);
  const [generateDifficulty, setGenerateDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const [generateQuestionCount, setGenerateQuestionCount] = useState<number | "">(8);

  // Set context when node loads
  useEffect(() => {
    if (node && projectId) {
      setCourseContext({
        courseId: projectId,
        courseName: courseName,
        nodeId: node.id,
        nodeName: node.title,
        pageType: "node-exam",
      });
    }
    
    return () => {
      clearCourseContext();
    };
  }, [node, projectId, courseName]);

  useEffect(() => {
    if (nodeId) {
      fetchNodeAndExam();
    }
  }, [nodeId]);

  // Fetch course name for context
  useEffect(() => {
    const fetchCourseName = async () => {
      if (projectId) {
        const { data } = await supabase
          .from("learning_projects")
          .select("title")
          .eq("id", projectId)
          .single();
        if (data) setCourseName(data.title);
      }
    };
    fetchCourseName();
  }, [projectId]);

  const fetchNodeAndExam = async () => {
    setLoading(true);
    try {
      // Fetch node details
      const { data: nodeData, error: nodeError } = await supabase
        .from("skill_nodes")
        .select("*")
        .eq("id", nodeId)
        .single();

      if (nodeError) throw nodeError;
      setNode(nodeData);

      // Fetch exam
      const { data: examData, error: examError } = await supabase
        .from("skill_node_exams")
        .select("*")
        .eq("skill_node_id", nodeId)
        .maybeSingle();

      if (examError && examError.code !== "PGRST116") throw examError;
      
      if (examData) {
        setExam(examData);
        setEditedPassingScore(examData.passing_score);
        if (examData.difficulty && ["easy", "medium", "hard"].includes(examData.difficulty)) {
          setGenerateDifficulty(examData.difficulty as "easy" | "medium" | "hard");
        }
        if (examData.question_count != null) setGenerateQuestionCount(examData.question_count);
        setEditedInstructions(examData.instructions || "");

        // Fetch questions (table column is exam_id; order by created_at for stable order)
        const { data: questionsData, error: questionsError } = await supabase
          .from("exam_questions")
          .select("*")
          .eq("exam_id", examData.id)
          .order("created_at", { ascending: true });

        if (questionsError) throw questionsError;
        // Normalize options: DB JSONB can be array or sometimes string; ensure we always have null or string[]
        const normalized = (questionsData || []).map((q: any) => ({
          ...q,
          options: q.options == null ? null : Array.isArray(q.options) ? q.options : (typeof q.options === "string" ? (() => { try { const p = JSON.parse(q.options); return Array.isArray(p) ? p : null; } catch { return null; } })() : null),
        }));
        setQuestions(normalized);
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading exam",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExam = async () => {
    if (!exam) return;

    setSaving(true);
    try {
      // Update exam settings
      const { error: examError } = await supabase
        .from("skill_node_exams")
        .update({
          passing_score: editedPassingScore,
          instructions: editedInstructions,
        })
        .eq("id", exam.id);

      if (examError) throw examError;

      // Update questions
      for (const question of questions) {
        const { error: questionError } = await supabase
          .from("exam_questions")
          .update({
            prompt: question.prompt,
            options: question.options,
            correct_answer: question.correct_answer,
            explanation: question.explanation,
            points: question.points,
            question_type: question.question_type,
          })
          .eq("id", question.id);

        if (questionError) throw questionError;
      }

      toast({
        title: "Saved!",
        description: "Exam has been saved successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error saving exam",
        description: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateExam = async (includeCapstone = false) => {
    if (!nodeId) return;

    setGenerating(true);
    try {
      const questionCount = typeof generateQuestionCount === "number" 
        ? Math.min(20, Math.max(1, generateQuestionCount)) 
        : undefined;
      const { data, error } = await invokeFunction("generate-node-exam", {
        body: { 
          skillNodeId: nodeId, 
          difficulty: generateDifficulty, 
          question_count: questionCount, 
          passing_score: editedPassingScore,
          include_capstone: includeCapstone,
        },
      });

      if (error) throw error;

      toast({
        title: "Exam generated!",
        description: includeCapstone
          ? "AI created questions including a capstone essay scenario."
          : "AI has created exam questions for this node.",
      });
      
      fetchNodeAndExam();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error generating exam",
        description: error.message,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleAddQuestion = async () => {
    if (!exam) return;

    try {
      const { data, error } = await supabase
        .from("exam_questions")
        .insert({
          exam_id: exam.id,
          question_type: "mcq",
          prompt: "New question",
          options: ["Option A", "Option B", "Option C", "Option D"],
          correct_answer: "Option A",
          explanation: "",
          points: 1,
        })
        .select()
        .single();

      if (error) throw error;

      setQuestions([...questions, data]);
      toast({
        title: "Question added",
        description: "New question has been added to the exam.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error adding question",
        description: error.message,
      });
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const { error } = await supabase
        .from("exam_questions")
        .delete()
        .eq("id", questionId);

      if (error) throw error;

      setQuestions(questions.filter(q => q.id !== questionId));
      toast({
        title: "Question deleted",
        description: "Question has been removed from the exam.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error deleting question",
        description: error.message,
      });
    }
  };

  const updateQuestion = (questionId: string, updates: Partial<ExamQuestion>) => {
    setQuestions(questions.map(q => 
      q.id === questionId ? { ...q, ...updates } : q
    ));
  };

  const updateQuestionOption = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(questions.map(q => {
      if (q.id !== questionId || !q.options) return q;
      const newOptions = [...q.options];
      newOptions[optionIndex] = value;
      return { ...q, options: newOptions };
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b sticky top-0 bg-background z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/project/${projectId}`)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                Manage Exam
              </h1>
              <p className="text-sm text-muted-foreground">{node?.title}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => handleGenerateExam(false)}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generating ? "Generating..." : "AI Generate"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleGenerateExam(true)}
              disabled={generating}
              title="Includes one novel-scenario capstone essay (AI-graded with other open-ended items)"
            >
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generating ? "Generating..." : "AI + capstone"}
            </Button>
            <Button onClick={handleSaveExam} disabled={saving || !exam}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid gap-6">
          {/* Node Info */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{node?.title}</CardTitle>
                  <CardDescription>
                    {node?.description || "No description provided"}
                  </CardDescription>
                </div>
                {node?.is_key_node ? (
                  <Badge variant="default">Key Node</Badge>
                ) : (
                  <Badge variant="secondary">Regular Node</Badge>
                )}
              </div>
            </CardHeader>
          </Card>

          {/* Generate options (when no exam or for re-generate) */}
          <Card>
            <CardHeader>
              <CardTitle>Quiz settings (for AI generate)</CardTitle>
              <CardDescription>
                Difficulty, number of questions, and passing score. Use <strong>AI + capstone</strong> in the header to add a novel-scenario essay item. Learners must pass this exam to complete the lesson.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Difficulty</Label>
                  <Select value={generateDifficulty} onValueChange={(v: "easy" | "medium" | "hard") => setGenerateDifficulty(v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number of questions</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={generateQuestionCount === "" ? "" : generateQuestionCount}
                    onChange={(e) => setGenerateQuestionCount(e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="5–10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passing score (%)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={editedPassingScore}
                    onChange={(e) => setEditedPassingScore(Number(e.target.value))}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Exam Settings */}
          {exam && (
            <Card>
              <CardHeader>
                <CardTitle>Exam Settings</CardTitle>
                <CardDescription>Configure exam parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="passing-score">Passing Score (%)</Label>
                    <Input
                      id="passing-score"
                      type="number"
                      min={0}
                      max={100}
                      value={editedPassingScore}
                      onChange={(e) => setEditedPassingScore(Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Points</Label>
                    <div className="h-10 px-3 py-2 border rounded-md bg-muted text-muted-foreground">
                      {questions.reduce((sum, q) => sum + q.points, 0)} points
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instructions">Instructions</Label>
                  <Textarea
                    id="instructions"
                    value={editedInstructions}
                    onChange={(e) => setEditedInstructions(e.target.value)}
                    placeholder="Enter exam instructions for students..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions */}
          {exam && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Questions ({questions.length})</h2>
                <Button onClick={handleAddQuestion} variant="outline" size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Question
                </Button>
              </div>

              {questions.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground mb-4">
                      No questions yet. Add questions manually or use AI to generate them.
                    </p>
                    <div className="flex gap-2 justify-center">
                      <Button onClick={handleAddQuestion} variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Add Question
                      </Button>
                      <Button onClick={() => handleGenerateExam(false)} disabled={generating}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        AI Generate
                      </Button>
                      <Button variant="secondary" onClick={() => handleGenerateExam(true)} disabled={generating}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        AI + capstone
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                questions.map((question, index) => (
                  <Card key={question.id}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                          <CardTitle className="text-base">Question {index + 1}</CardTitle>
                          <Badge variant="outline">{question.question_type}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min={1}
                            value={question.points}
                            onChange={(e) => updateQuestion(question.id, { points: Number(e.target.value) })}
                            className="w-20"
                          />
                          <span className="text-sm text-muted-foreground">pts</span>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Question</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this question?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteQuestion(question.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Question Type</Label>
                        <Select
                          value={question.question_type}
                          onValueChange={(value: "mcq" | "true_false" | "short_answer" | "fill_in_blank" | "capstone_essay") => 
                            updateQuestion(question.id, { 
                              question_type: value,
                              options: value === "mcq" ? ["Option A", "Option B", "Option C", "Option D"] :
                                       value === "true_false" ? ["True", "False"] : null,
                              points: value === "capstone_essay" ? Math.max(question.points, 8) : question.points,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mcq">Multiple Choice</SelectItem>
                            <SelectItem value="true_false">True/False</SelectItem>
                            <SelectItem value="short_answer">Short Answer (scenario)</SelectItem>
                            <SelectItem value="fill_in_blank">Fill in the blank</SelectItem>
                            <SelectItem value="capstone_essay">Capstone essay (scenario)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Question Prompt</Label>
                        <Textarea
                          value={question.prompt}
                          onChange={(e) => updateQuestion(question.id, { prompt: e.target.value })}
                          rows={2}
                        />
                      </div>

                      {Array.isArray(question.options) && question.options.length > 0 && (
                        <div className="space-y-2">
                          <Label>Options</Label>
                          {question.options.map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center gap-2">
                              <Input
                                value={option}
                                onChange={(e) => updateQuestionOption(question.id, optionIndex, e.target.value)}
                                className={question.correct_answer === option ? "border-green-500" : ""}
                              />
                              <Button
                                variant={question.correct_answer === option ? "default" : "outline"}
                                size="sm"
                                onClick={() => updateQuestion(question.id, { correct_answer: option })}
                              >
                                {question.correct_answer === option ? "✓ Correct" : "Set Correct"}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {(question.question_type === "short_answer" || question.question_type === "fill_in_blank" || question.question_type === "capstone_essay") && (
                        <div className="space-y-2">
                          <Label>
                            {question.question_type === "capstone_essay"
                              ? "Model answer (reference for AI grader)"
                              : question.question_type === "fill_in_blank"
                                ? "Correct answer (exact fill-in)"
                                : "Model answer / key points"}
                          </Label>
                          {question.question_type === "capstone_essay" ? (
                            <Textarea
                              value={question.correct_answer}
                              onChange={(e) => updateQuestion(question.id, { correct_answer: e.target.value })}
                              rows={8}
                              placeholder="Full-credit expert response the grader will compare against..."
                            />
                          ) : (
                            <Input
                              value={question.correct_answer}
                              onChange={(e) => updateQuestion(question.id, { correct_answer: e.target.value })}
                            />
                          )}
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Explanation (shown after answer)</Label>
                        <Textarea
                          value={question.explanation || ""}
                          onChange={(e) => updateQuestion(question.id, { explanation: e.target.value })}
                          rows={2}
                          placeholder="Explain the correct answer..."
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* No Exam Yet */}
          {!exam && node && (
            <Card>
              <CardContent className="py-12 text-center">
                <ClipboardCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Exam Created Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Use AI to generate an exam or create questions manually.
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  <Button onClick={() => handleGenerateExam(false)} disabled={generating}>
                    {generating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    Generate with AI
                  </Button>
                  <Button variant="secondary" onClick={() => handleGenerateExam(true)} disabled={generating}>
                    {generating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="mr-2 h-4 w-4" />
                    )}
                    AI + capstone
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* AI Generation Tip */}
          {exam && (
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Regenerate with AI</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Click "AI Generate" to regenerate exam questions based on the lesson content.
                      This will replace existing questions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageNodeExam;

